/**
 * Browser-side Meta CompleteRegistration for a brand-new account (#767).
 * event_id is deterministic from the user id so a future server-side (CAPI) copy can
 * recompute the same id and Meta dedups the pair without any shared storage.
 */

const NEW_USER_WINDOW_MINUTES = 5;
const FIRED_KEY_PREFIX = 'bt_reg_fired_';

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function registrationEventId(userId: string): string {
  return `reg_${userId}`;
}

export function isNewAccount(createdAt: string | number | Date | undefined, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return now - created < NEW_USER_WINDOW_MINUTES * 60_000;
}

/**
 * Fires once per user per browser session (setAuthenticatedUser re-runs on reload inside
 * the new-user window). `fire` receives the event_id.
 */
export function fireRegistrationOnce(
  user: { _id: string; createdAt?: string | number | Date },
  fire: (eventId: string) => void,
  store: KeyValueStore | null,
  now: number = Date.now(),
): boolean {
  if (!user._id || !isNewAccount(user.createdAt, now)) return false;
  const key = FIRED_KEY_PREFIX + user._id;
  try {
    if (store?.getItem(key)) return false;
    store?.setItem(key, '1');
  } catch {
    // storage blocked: fall through and fire (at worst one duplicate, deduped by event_id)
  }
  fire(registrationEventId(user._id));
  return true;
}
